<template>
  <div class="coin-flow-management">
    <el-card shadow="never" class="animate__animated animate__fadeInUp">
      <div class="filter-card">
        <div class="filter-bar">
          <div class="filter-group">
            <el-input
              v-model="searchUserId"
              placeholder="用户ID"
              clearable
              class="filter-input-sm"
              @keyup.enter.native="handleSearch"
            />
            <el-input
              v-model="searchOperator"
              placeholder="操作人"
              clearable
              class="filter-input-sm"
              @keyup.enter.native="handleSearch"
            />
            <el-select
              v-model="searchFlowType"
              placeholder="流水类型"
              clearable
              class="filter-select"
              @change="handleSearch"
            >
              <el-option label="全部类型" value="" />
              <el-option label="后台调账" value="admin_adjust" />
              <el-option label="用户观看奖励" value="user_reward" />
              <el-option label="直推奖励" value="direct" />
              <el-option label="间推奖励" value="indirect" />
              <el-option label="代理收益" value="agent" />
              <el-option label="提现申请" value="withdraw" />
              <el-option label="提现退还" value="withdraw_refund" />
              <el-option label="删除用户清零" value="user_delete" />
            </el-select>
            <el-date-picker
              v-model="searchStartDate"
              type="datetime"
              placeholder="开始时间"
              value-format="yyyy-MM-dd HH:mm:ss"
              clearable
              class="filter-date"
              @change="handleSearch"
            />
            <el-date-picker
              v-model="searchEndDate"
              type="datetime"
              placeholder="结束时间"
              value-format="yyyy-MM-dd HH:mm:ss"
              clearable
              class="filter-date"
              @change="handleSearch"
            />
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
            <el-button icon="el-icon-refresh" @click="refreshList">刷新</el-button>
          </div>
          <div class="action-group">
            <el-button
              type="success"
              icon="el-icon-download"
              :loading="exportLoading"
              :disabled="loading"
              @click="handleExport"
            >导出Excel</el-button>
          </div>
        </div>
      </div>

      <div v-if="error" class="error">{{ error }}</div>

      <div v-else class="list-section">
        <div class="summary-bar">
          <span class="summary-label">变动金币合计</span>
          <span
            class="summary-value"
            :class="{
              'amount-plus': coinAmountSum > 0,
              'amount-minus': coinAmountSum < 0
            }"
          >{{ formatCoinSum(coinAmountSum) }}</span>
          <span class="summary-hint">（当前筛选条件下全部匹配记录）</span>
        </div>

        <el-table
          :data="list"
          border
          stripe
          v-loading="loading"
          element-loading-text="正在加载..."
          class="coin-flow-table animate__animated animate__fadeIn"
          row-key="id"
        >
          <el-table-column prop="id" label="ID" width="160" align="center" />
          <el-table-column prop="userId" label="用户ID" width="180" align="center">
            <template slot-scope="scope">
              {{ scope.row.userId != null ? scope.row.userId : '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="coinAmount" label="变动金币" width="200" align="center">
            <template slot-scope="scope">
              <span v-if="scope.row.coinAmount > 0" class="amount-plus">+{{ scope.row.coinAmount }}</span>
              <span v-else-if="scope.row.coinAmount < 0" class="amount-minus">{{ scope.row.coinAmount }}</span>
              <span v-else>{{ scope.row.coinAmount != null ? scope.row.coinAmount : '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="flowType" label="流水类型" min-width="220" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.flowType === 'user_reward'" type="success" size="small">用户观看奖励</el-tag>
              <el-tag v-else-if="scope.row.flowType === 'direct'" type="primary" size="small">直推奖励</el-tag>
              <el-tag v-else-if="scope.row.flowType === 'indirect'" type="info" size="small">间推奖励</el-tag>
              <el-tag v-else-if="scope.row.flowType === 'agent'" type="warning" size="small">代理收益</el-tag>
              <el-tag v-else-if="scope.row.flowType === 'withdraw'" type="primary" size="small">提现申请</el-tag>
              <el-tag v-else-if="scope.row.flowType === 'withdraw_refund'" type="danger" size="small">提现退还</el-tag>
              <el-tag v-else-if="scope.row.flowType === 'admin_adjust'" type="warning" size="small">后台调账</el-tag>
              <el-tag v-else-if="scope.row.flowType === 'user_delete'" type="danger" size="small">删除用户清零</el-tag>
              <span v-else>{{ scope.row.flowType }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="operator" label="操作人" width="200" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ scope.row.operator || '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="description" label="描述" min-width="320" show-overflow-tooltip />
          <el-table-column prop="createdTime" label="操作时间" width="200" align="center" show-overflow-tooltip />
        </el-table>

        <div v-if="!loading && list.length === 0" class="empty-state">
          <el-empty :description="searchUserId || searchOperator || searchFlowType || searchStartDate || searchEndDate ? '没有符合条件的记录' : '暂无金币流水数据'" />
        </div>

        <div v-if="pagination.total > 0" class="pagination-container">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="pagination.page"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            @current-change="handlePageChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { get } from '@/utils/http'
import { downloadExcel } from '@/utils/excel'

const FLOW_TYPE_LABELS = {
  admin_adjust: '后台调账',
  user_reward: '用户观看奖励',
  direct: '直推奖励',
  indirect: '间推奖励',
  agent: '代理收益',
  withdraw: '提现申请',
  withdraw_refund: '提现退还',
  user_delete: '删除用户清零'
}

export default {
  name: 'CoinFlowManagement',
  data() {
    const now = new Date()
    const today = this.formatDateStr(now)
    return {
      list: [],
      loading: false,
      exportLoading: false,
      error: '',
      coinAmountSum: 0,
      searchUserId: '',
      searchOperator: '',
      searchFlowType: '',
      searchStartDate: today + ' 00:00:00',
      searchEndDate: today + ' 23:59:59',
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    formatDateStr(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },

    formatFlowType(flowType) {
      return FLOW_TYPE_LABELS[flowType] || flowType || '-'
    },

    formatCoinSum(sum) {
      const value = Number(sum) || 0
      if (value > 0) {
        return '+' + value
      }
      return String(value)
    },

    buildQueryParams(page, pageSize) {
      const params = { page, pageSize }
      if (this.searchUserId) {
        params.userId = this.searchUserId
      }
      if (this.searchOperator) {
        params.operator = this.searchOperator
      }
      if (this.searchFlowType) {
        params.flowType = this.searchFlowType
      }
      params.startDate = this.searchStartDate
      params.endDate = this.searchEndDate
      return params
    },

    async fetchList() {
      this.loading = true
      this.error = ''
      try {
        const result = await get('/admin/coin-flow', this.buildQueryParams(this.pagination.page, this.pagination.pageSize))
        const data = result.data || {}
        this.list = Array.isArray(data.records) ? data.records : []
        this.pagination.total = data.total || 0
        this.pagination.page = data.page || this.pagination.page
        this.pagination.pageSize = data.pageSize || this.pagination.pageSize
        this.coinAmountSum = data.coinAmountSum != null ? Number(data.coinAmountSum) : 0
      } catch (e) {
        this.error = e && e.message ? e.message : '获取金币流水失败'
        this.list = []
        this.pagination.total = 0
        this.coinAmountSum = 0
      } finally {
        this.loading = false
      }
    },

    async handleExport() {
      this.exportLoading = true
      try {
        const allRecords = []
        let page = 1
        const pageSize = 200
        let hasMore = true

        while (hasMore) {
          const result = await get('/admin/coin-flow', this.buildQueryParams(page, pageSize))
          const data = (result && result.data) ? result.data : {}
          const records = Array.isArray(data.records) ? data.records : []
          if (records.length > 0) {
            allRecords.push(...records)
            if (records.length < pageSize) {
              hasMore = false
            } else {
              page++
            }
          } else {
            hasMore = false
          }
          if (data.total && allRecords.length >= data.total) {
            hasMore = false
          }
        }

        if (allRecords.length === 0) {
          this.$message.warning('当前条件下没有可导出的数据')
          return
        }

        const exportData = allRecords.map(row => ({
          'ID': row.id != null ? row.id : '-',
          '用户ID': row.userId != null ? row.userId : '-',
          '变动金币': row.coinAmount != null ? row.coinAmount : '-',
          '流水类型': this.formatFlowType(row.flowType),
          '操作人': row.operator || '-',
          '描述': row.description || '-',
          '操作时间': row.createdTime || '-'
        }))

        const searchInfo = []
        if (this.searchUserId) searchInfo.push(`用户ID_${this.searchUserId}`)
        if (this.searchOperator) searchInfo.push(`操作人_${this.searchOperator}`)
        if (this.searchFlowType) searchInfo.push(`类型_${this.formatFlowType(this.searchFlowType)}`)
        if (this.searchStartDate) searchInfo.push(`开始_${this.searchStartDate.replace(/[:\s]/g, '')}`)
        if (this.searchEndDate) searchInfo.push(`结束_${this.searchEndDate.replace(/[:\s]/g, '')}`)
        const fileName = `金币明细_${searchInfo.length > 0 ? searchInfo.join('_') : '全部'}_${Date.now()}.xlsx`

        downloadExcel(exportData, '金币明细', fileName, [
          { wch: 10 }, { wch: 10 }, { wch: 12 },
          { wch: 14 }, { wch: 14 }, { wch: 30 }, { wch: 20 }
        ])
        this.$message.success(`成功导出 ${allRecords.length} 条记录`)
      } catch (error) {
        this.$message.error('导出失败: ' + (error.message || '未知错误'))
      } finally {
        this.exportLoading = false
      }
    },

    handleSearch() {
      this.pagination.page = 1
      this.fetchList()
    },

    handlePageChange(page) {
      this.pagination.page = page
      this.fetchList()
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.fetchList()
    },

    refreshList() {
      this.searchUserId = ''
      this.searchOperator = ''
      this.searchFlowType = ''
      const today = this.formatDateStr(new Date())
      this.searchStartDate = today + ' 00:00:00'
      this.searchEndDate = today + ' 23:59:59'
      this.pagination.page = 1
      this.fetchList()
    }
  }
}
</script>

<style scoped>
.filter-card {
  margin-bottom: 20px;
}

.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.filter-group,
.action-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.filter-input-sm {
  width: 120px;
}

.filter-select {
  width: 150px;
}

.filter-date {
  width: 200px;
}

.coin-flow-table {
  width: 100%;
  margin-bottom: 8px;
}

.summary-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 14px;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #ebeef5;
  border-radius: 6px;
}

.summary-label {
  color: #606266;
  font-size: 14px;
}

.summary-value {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  line-height: 1.2;
}

.summary-hint {
  color: #909399;
  font-size: 12px;
}

.coin-flow-table ::v-deep .el-table__header th,
.coin-flow-table ::v-deep .el-table__body td {
  padding: 12px 14px;
}

.coin-flow-table ::v-deep .cell {
  line-height: 1.5;
  padding-left: 10px;
  padding-right: 10px;
}

.amount-plus {
  color: #67c23a;
  font-weight: 500;
}

.amount-minus {
  color: #f56c6c;
  font-weight: 500;
}

.error {
  color: #f87171;
  padding: 10px;
  background-color: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 4px;
  margin-bottom: 20px;
}

.empty-state {
  text-align: center;
  padding: 40px 0;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
</style>
